import { useT } from "@/i18n";
import {
  AnswerLine,
  EmptyState,
  FeedbackBox,
  PromptAudio,
  PromptVideo,
  ReadOnlyCard,
  SpeakerButton,
} from "./ReadOnlyKit";
import { SPACING } from "./readOnlyStyles";
import type { ReadOnlyTemplateProps } from "./types";
import { useReviewAudio } from "./useReviewAudio";

const checkpoint = {
  display: "flex",
  flexDirection: "column",
  gap: SPACING.xs,
  border: "1px solid #ECEBF5",
  borderRadius: 16,
  padding: SPACING.md,
} as const;

const questionRow = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: SPACING.sm,
} as const;

const numberBadge = {
  display: "flex",
  flexShrink: 0,
  width: 24,
  height: 24,
  borderRadius: 12,
  backgroundColor: "#EEEDFB",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 800,
  color: "#2C2B80",
} as const;

const questionText = {
  flex: 1,
  fontSize: 15,
  lineHeight: "22px",
  color: "#2C2B80",
  fontWeight: 600,
  margin: 0,
} as const;

/** A past video-question play: each checkpoint's question, the answer spoken to it, and the clip. */
export function VideoQuestionReadOnly({
  snapshot,
  response,
  score,
  recordingUrl,
}: ReadOnlyTemplateProps<"videoQuestion">) {
  const t = useT();
  const { checkpoints, mediaType, video, audio } = snapshot.slideData;
  const { playingUrl, toggle } = useReviewAudio();

  return (
    <ReadOnlyCard title={snapshot.name} score={score}>
      {mediaType === "audio" ? (
        <PromptAudio
          media={audio}
          label={t["readonly.theClip"]}
          isPlaying={playingUrl === audio.src}
          onPress={() => toggle(audio.src ?? null)}
        />
      ) : (
        <PromptVideo media={video} label={t["readonly.promptVideo"]} />
      )}

      {response.checkpoints.length === 0 ? (
        <EmptyState message={t["readonly.noCheckpoints"]} />
      ) : (
        response.checkpoints.map((answered, index) => {
          const authored = checkpoints.find((item) => item.timestamp === answered.timestamp);
          const myRecording = recordingUrl(answered.take?.recordingId);

          return (
            <div key={`${answered.timestamp}-${index}`} style={checkpoint}>
              <div style={questionRow}>
                <span style={numberBadge}>{index + 1}</span>
                <p style={questionText}>{authored?.question || t["readonly.questionNotSet"]}</p>
              </div>
              <AnswerLine
                label={t["readonly.answer"]}
                studentAnswer={answered.recognizedText}
                score={answered.score}
                accessory={
                  <SpeakerButton
                    label={t["readonly.myRecording"]}
                    url={myRecording}
                    isPlaying={playingUrl === myRecording}
                    onPress={() => toggle(myRecording)}
                    variant="student"
                  />
                }
              />
              {answered.explanation ? <FeedbackBox text={answered.explanation} /> : null}
            </div>
          );
        })
      )}
    </ReadOnlyCard>
  );
}
